import React from 'react';
import { motion } from 'framer-motion';
import { fadeIn } from '../../../lib/motion';
import { isPending } from '../../courses/editors/contentPlaceholder';
import ModuleViewContainer from './ModuleViewContainer';

export interface LinkViewProps {
  item: { meeting_url?: string; text?: string; title?: string };
  mode: 'learner' | 'preview';
}

/** Known providers block plain page URLs inside iframes but offer embed
 *  routes — rewrite what we can. Everything else is served best-effort. */
function toEmbeddableUrl(raw: string): string {
  try {
    const u = new URL(raw);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
      const shorts = u.pathname.match(/^\/shorts\/([\w-]+)/);
      if (shorts) return `https://www.youtube.com/embed/${shorts[1]}`;
    }
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1);
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host === 'vimeo.com') {
      const id = u.pathname.match(/^\/(\d+)/);
      if (id) return `https://player.vimeo.com/video/${id[1]}`;
    }
    return raw;
  } catch {
    return raw;
  }
}

const LinkView: React.FC<LinkViewProps> = ({ item, mode }) => {
  const rawUrl = item.meeting_url && !isPending(item.meeting_url) ? item.meeting_url : null;
  const url = rawUrl ? toEmbeddableUrl(rawUrl) : null;
  const label = item.text || item.title || 'Embedded content';

  // Same card shape as RichTextView's learner body: flex-fill rounded card
  // on `bg-surface-primary` with the soft drop-shadow and inner padding.
  const body = url ? (
    <div className="flex-1 min-h-0 flex flex-col rounded-2xl bg-surface-primary shadow-[0_4px_10px_rgba(0,0,0,0.1)] p-4 sm:p-6 overflow-hidden gap-3">
      {/* Many sites refuse to render inside other apps (X-Frame-Options /
          frame-ancestors) and fail silently — the new-tab link is the
          guaranteed path to the content, the iframe a best effort. */}
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-text-primary truncate">{label}</span>
        <a
          href={rawUrl ?? url}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.97] transition-[background-color,transform] duration-150"
        >
          Open in new tab
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M7 17L17 7M9 7h8v8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      </div>
      <iframe
        src={url}
        title={label}
        className="w-full flex-1 min-h-0 rounded-lg border-0"
        allowFullScreen
        allow="accelerometer; autoplay; camera; microphone; display-capture; clipboard-read; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  ) : (
    <div className="flex-1 min-h-0 flex items-center justify-center rounded-2xl bg-surface-primary shadow-[0_4px_10px_rgba(0,0,0,0.1)] p-4 sm:p-6">
      <span className="text-sm text-text-tertiary">No link set</span>
    </div>
  );

  if (mode === 'learner') {
    return (
      <motion.div {...fadeIn} className="w-full flex-1 flex flex-col min-h-0">
        <ModuleViewContainer padding="px-4 pb-4">
          {item.title && (
            <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3">
              <h2 className="text-lg sm:text-xl font-bold text-text-primary leading-tight">
                {item.title}
              </h2>
            </div>
          )}
          <div className="flex-1 flex flex-col min-h-0 px-3 sm:px-4 pb-3 sm:pb-4">
            {body}
          </div>
        </ModuleViewContainer>
      </motion.div>
    );
  }

  return (
    <motion.div {...fadeIn} className="w-full">
      {body}
    </motion.div>
  );
};

export default LinkView;
